import { createRouter, createWebHistory } from 'vue-router'
import { usePermissions } from '@/composables/usePermissions'
import { RAW_TABLE_PERMISSION_KEYS } from '@/config/rawTables'
import { canEnterPmsSettings } from '@/utils/nativePmsUi'
import { HOTEL_OPTIMISATION_TAB_KEYS } from '@/utils/hotelOptimisationTabs'
import { useAuthStore } from '@/stores/auth'
import { usePostHog } from '@/composables/usePostHog'
import { setAuthenticatedUser, setCurrentRoute } from '@/services/telemetryService'

// After a deploy the running SPA still references the old chunk hashes
// (administration-XYZ.js etc.); navigating to a lazy-loaded route fails
// with "Failed to fetch dynamically imported module". Auto-reload once
// to pick up the new index.html + chunk manifest. The sessionStorage
// gate stops an infinite loop if the failure persists (e.g. real network
// error rather than a stale bundle).
const STALE_CHUNK_RELOAD_KEY = 'eh:stale-chunk-reload-at'
function isStaleChunkError(err: unknown): boolean {
  const msg = (err as any)?.message || String(err || '')
  return /Failed to fetch dynamically imported module/i.test(msg)
      || /error loading dynamically imported module/i.test(msg)
      || /Importing a module script failed/i.test(msg)
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/health',
      name: 'health',
      component: () => import('@/pages/health.vue'),
      meta: { requiresAuth: false }
    },
    {
      // /test — public sandbox page; doubles as a deploy-landing canary.
      path: '/test',
      name: 'test',
      component: () => import('@/pages/test.vue'),
      meta: { requiresAuth: false }
    },
    {
      // /go-live — countdown to 1 January 2027. Public, no login required.
      path: '/go-live',
      name: 'go-live',
      component: () => import('@/pages/go-live.vue'),
      meta: { requiresAuth: false }
    },
    {
      // Customer-facing EVENT portal (POC). Public — a customer manages
      // their event (proposal/prices, participants) with no login. The
      // :code param is the future unique access token; today it just
      // renders the demo. Lives OUTSIDE the authenticated DashboardView
      // shell so there's no app sidebar/chrome.
      path: '/portal/event/:code',
      name: 'portal-event',
      component: () => import('@/pages/portal-event.vue'),
      meta: { requiresAuth: false }
    },
    {
      // Customer-facing RESERVATION portal (POC). Public — a guest views
      // details / storno terms and can cancel. :code = future access token.
      path: '/portal/reservation/:code',
      name: 'portal-reservation',
      component: () => import('@/pages/portal-reservation.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/api-docs',
      name: 'api-docs',
      component: () => import('@/pages/api-docs.vue'),
      meta: { requiresAuth: true }
    },
    {
      // /docs/reservation-routing — internal docs: how the backend routes a
      // hotel-participant pickup to a room category / block (hard cap, EZ→DZ
      // steering, no-availability outcomes). Standalone full-page, like /api-docs.
      path: '/docs/reservation-routing',
      name: 'reservation-routing',
      component: () => import('@/pages/reservation-routing.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/calendar-only',
      name: 'calendar-only',
      component: () => import('@/pages/calendar-only.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          // Root path. The router guard below redirects 'home' to
          // firstAccessiblePage() — which prefers 'dashboard' when the
          // role has it (it sits first in allPages), otherwise falls
          // back to 'events'. Component is events.vue purely as a
          // safety net for the rare flash before the redirect resolves;
          // the guard handles the real routing.
          path: '',
          name: 'home',
          component: () => import('@/pages/events.vue')
        },
        {
          path: 'events',
          name: 'events',
          component: () => import('@/pages/events.vue')
        },
        {
          // Event-Management — planning cockpit for event managers.
          // Defaults to the ADG Seminare stream; charts events per
          // event manager, scores each event's value (pax × days) and
          // surfaces what to re-plan next year. Gated by `event-management`.
          path: 'event-management',
          name: 'event-management',
          component: () => import('@/pages/event-management.vue')
        },
        {
          // Business School — only `bs` events, their participants as
          // "Studierende", by programme / semester. Gated by `business-school`.
          path: 'business-school',
          name: 'business-school',
          component: () => import('@/pages/business-school.vue')
        },
        {
          // Role-aware dashboard landing. Permission-gated via the
          // standard role_permissions row (page_key='dashboard'); when
          // granted, becomes the user's default landing on '/'.
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/pages/dashboard.vue')
        },
        {
          path: 'observability',
          name: 'observability',
          component: () => import('@/pages/observability.vue')
        },
        {
          path: 'user-experience',
          name: 'user-experience',
          component: () => import('@/pages/user-experience.vue')
        },
        {
          path: 'event-bookings',
          redirect: '/events'
        },
        {
          path: 'event-sessions',
          name: 'event-sessions',
          component: () => import('@/pages/event-sessions.vue')
        },
        {
          path: 'attendees',
          name: 'attendees',
          component: () => import('@/pages/attendees.vue')
        },
        {
          path: 'spaces',
          name: 'spaces',
          component: () => import('@/pages/spaces.vue')
        },
        {
          path: 'availabilities',
          name: 'availabilities',
          component: () => import('@/pages/availabilities.vue')
        },
        {
          // Block Manager is a TAB of Hotel Optimisation now, not a page. The
          // old path stays as a redirect so bookmarks, the menu history and any
          // link written before the move keep working.
          path: 'block-manager',
          redirect: { name: 'hotel-optimisation', query: { tab: 'block-manager' } }
        },
        {
          path: 'reservations',
          name: 'reservations',
          component: () => import('@/pages/reservations.vue')
        },
        {
          // Reception working surface (arrivals / in-house / departures).
          // Permission-gated via the `pms-board` pageKey in config/permissions.
          // `standalone` renders it WITHOUT the left menu / app chrome — it's
          // opened from the dashboard as its own little app (new tab).
          path: 'pms-board',
          name: 'pms-board',
          meta: { standalone: true },
          component: () => import('@/pages/pms-board.vue')
        },
        {
          path: 'room-occupancy',
          name: 'room-occupancy',
          component: () => import('@/pages/room-occupancy.vue')
        },
        {
          path: 'booking-plan',
          name: 'booking-plan',
          component: () => import('@/pages/booking-plan.vue')
        },
        {
          path: 'menus',
          name: 'menus',
          component: () => import('@/pages/menus.vue')
        },
        {
          // Optional video id → a shareable deep link that opens that video
          // directly (e.g. /inside-eventhub/42); bare path shows the library.
          path: 'inside-eventhub/:videoId(\\d+)?',
          name: 'inside-eventhub',
          component: () => import('@/pages/inside-eventhub.vue')
        },
        {
          // What each room sign shows at a chosen time (signage feed).
          path: 'signage-emulator',
          name: 'signage-emulator',
          component: () => import('@/pages/signage-emulator.vue')
        },
        {
          path: 'exporter',
          name: 'exporter',
          component: () => import('@/pages/exporter.vue')
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('@/pages/contacts.vue')
        },
        {
          path: 'property-setup',
          name: 'property-setup',
          component: () => import('@/pages/property-setup.vue')
        },
        {
          // Native-PMS master data (property, categories, rooms, rates,
          // products, accounting categories, tax codes) on one full page.
          // Entry: any of PMS_SETTINGS_ENTRY_KEYS (see the guard below); the
          // page itself explains when the backend is not on the native PMS.
          path: 'pms-settings',
          name: 'pms-settings',
          component: () => import('@/pages/pms-settings.vue')
        },
        {
          path: 'administration',
          name: 'administration',
          component: () => import('@/pages/administration.vue')
        },
        {
          path: 'mews-services',
          name: 'mews-services',
          component: () => import('@/pages/mews-services.vue')
        },
        {
          path: 'analytics',
          name: 'analytics',
          component: () => import('@/pages/analytics.vue')
        },
        {
          path: 'stock-management',
          name: 'stock-management',
          component: () => import('@/pages/stock-management.vue')
        },
        {
          path: 'mice-board',
          name: 'mice-board',
          component: () => import('@/pages/mice-board.vue')
        },
        {
          path: 'mice-analytics',
          name: 'mice-analytics',
          component: () => import('@/pages/mice-analytics.vue')
        },
        // /roles-permissions route removed — Users and Roles tabs
        // now live inside /administration (gated by users-management /
        // roles-management permission keys). If anyone has a bookmark,
        // they'll get the standard "no access / first accessible page"
        // fallback from the global router guard.
        {
          path: 'masterdata-chain',
          name: 'masterdata-chain',
          component: () => import('@/pages/masterdata-chain.vue')
        },
        {
          // Data Management — cross-system data viewer + mismatch
          // surface (MEWS / BC live; Moodle / Magento queued).
          path: 'data-management',
          name: 'data-management',
          component: () => import('@/pages/data-management.vue')
        },
        {
          path: 'middleware',
          name: 'middleware',
          component: () => import('@/pages/middleware.vue')
        },
        {
          path: 'audit-log',
          name: 'audit-log',
          component: () => import('@/pages/audit-log.vue')
        },
        {
          path: 'error-log',
          name: 'error-log',
          component: () => import('@/pages/error-log.vue')
        },
        {
          path: 'conflict-resolution',
          name: 'conflict-resolution',
          component: () => import('@/pages/conflict-resolution.vue')
        },
        {
          path: 'hotel-optimisation',
          name: 'hotel-optimisation',
          component: () => import('@/pages/hotel-optimisation.vue')
        },
        {
          path: 'revenue-management',
          name: 'revenue-management',
          component: () => import('@/pages/revenue-management.vue')
        },
        {
          path: 'fnb-equipment',
          name: 'fnb-equipment',
          component: () => import('@/pages/fnb-equipment.vue')
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('@/pages/reports.vue')
        },
        {
          // Read-only PostgREST browser. Entry is granted by holding
          // `raw-tables` (the parent key) OR any `raw:<table>` sub-key
          // — the page itself filters the dropdown to tables the user
          // can actually access. See router guard below.
          path: 'raw-tables',
          name: 'raw-tables',
          component: () => import('@/pages/raw-tables.vue')
        },
        {
          // Azure DevOps PBI viewer — Linear-style ticket page. The
          // operator's PAT lives in localStorage; we never carry it
          // through the backend. Gated by `azure-pbis`.
          path: 'azure-pbis',
          name: 'azure-pbis',
          component: () => import('@/pages/azure-pbis.vue')
        },
        {
          // Backend E2E matrix viewer — embeds the localhost:3010 page
          // the backend repo serves via `npm run test:e2e:viewer`. Lives
          // under Dev Ops alongside the other diagnostic tabs.
          path: 'e2e-tester',
          name: 'e2e-tester',
          component: () => import('@/pages/e2e-tester.vue')
        },
        {
          // Dev Ops — tabbed shell that consolidates Error Log,
          // Azure DevOps, Observability and the E2E Tester under one
          // menu entry. Entry is granted by ANY of the sub-permissions;
          // each tab is hidden when its own key isn't held. Same
          // strict-per-tab pattern as /administration.
          path: 'dev-ops',
          name: 'dev-ops',
          component: () => import('@/pages/dev-ops.vue')
        },
        {
          // Block Pre-Flight — its own route as well as a Dev Ops tab, so it
          // can be linked to directly (it is the kind of page someone sends a
          // colleague when a week looks tight).
          path: 'block-pre-flight',
          name: 'block-pre-flight',
          component: () => import('@/pages/block-pre-flight.vue')
        },
        {
          // Roadmap — marketing-style product trajectory page intended
          // for sales demos. Gated by the `roadmap` permission so
          // admins can grant broadly.
          path: 'roadmap',
          name: 'roadmap',
          component: () => import('@/pages/roadmap.vue')
        }
        // /themes route removed — Themes is now a tab inside
        // /administration (gated by the existing `themes` page key).
      ]
    }
  ]
})

// Pages that don't require permission checks (always accessible if authenticated).
// The public customer portals are listed too so a logged-in staff member who
// opens a portal link isn't bounced by the permission guard.
const permissionExemptPages = ['login', 'health', 'calendar-only', 'api-docs', 'portal-event', 'portal-reservation']

router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.meta.requiresAuth !== false
  const auth = useAuthStore()
  await auth.init()
  const sessionExists = auth.isAuthenticated

  console.log('Router guard:', { path: to.path, requiresAuth, sessionExists })

  if (requiresAuth && !sessionExists) {
    console.log('Redirecting to login - auth required but no session')
    next('/login')
    return
  }

  if (sessionExists && auth.userId) {
    setAuthenticatedUser(auth.userId)
  }

  // Permission check for authenticated routes
  if (sessionExists && to.name && !permissionExemptPages.includes(to.name as string)) {
    const { loaded, loadPermissions, canAccess, firstAccessiblePage } = usePermissions()
    if (!loaded.value) {
      await loadPermissions()
    }

    // Root route ('/'). Redirect to first accessible page — which is
    // 'dashboard' when the role has it (it sits first in allPages),
    // else falls back to 'events' (next entry in the list). Same
    // behaviour as before for roles without dashboard access; new
    // landing experience for roles with it.
    if (to.name === 'home' && loaded.value) {
      const firstPage = firstAccessiblePage()
      if (firstPage) {
        console.log('Redirecting home to first accessible page:', firstPage)
        next({ name: firstPage })
        return
      }
      // If no accessible page, let DashboardView handle the no-permissions modal
      next()
      return
    }

    // Administration is now a six-tab page. Entry is granted if the
    // role has access to ANY tab (admin-*, users-management, or
    // roles-management). The page itself hides individual tabs the
    // user can't manage via its per-tab `permKey` filter. The legacy
    // `administration` umbrella key is still honored so existing rows
    // in role_permissions keep working without backfill.
    // Tabbed pages whose entry guard is satisfied by ANY of their tab
    // keys. Both lost their legacy umbrella (`administration` /
    // `property-setup`) in the strict-per-tab migration; access is now
    // granted purely by holding at least one tab key.
    const ADMIN_TAB_KEYS = [
      'admin-properties',
      'admin-integrations',
      'admin-currencies',
      'admin-property-settings',
      'users-management',
      'roles-management',
      'themes',
      'admin-exporter',
      'admin-raw-tables',
    ]
    const PROPERTY_SETUP_TAB_KEYS = [
      'ps-tab-spaces',
      'ps-tab-rates',
      'ps-tab-room-types',
      'ps-tab-accounting-categories',
      'ps-tab-tax-codes',
      'ps-tab-products',
      'ps-tab-packages',
      'ps-tab-price-lists',
      'ps-tab-rules',
      'ps-tab-webhooks',
      'ps-tab-contacts',
      'ps-tab-mews',
      'ps-tab-series',
      'ps-tab-tags',
    ]
    // raw-tables follows the same any-of pattern: the parent `raw-tables`
    // key OR any `raw:<table>` sub-key satisfies entry. The page filters
    // the dropdown to the tables the user can actually access.
    // Derived from the table config so a new raw table cannot be reachable in
    // the page yet bounced at the route (the literal list had drifted by ten
    // tables).
    const RAW_TABLE_KEYS = ['raw-tables', ...RAW_TABLE_PERMISSION_KEYS]
    // /dev-ops is a tabbed shell over error-log, azure-pbis,
    // observability and the e2e-tester. Entry granted by ANY tab key
    // — same as the administration / property-setup pattern.
    const DEV_OPS_TAB_KEYS = ['error-log', 'azure-pbis', 'observability', 'user-experience', 'e2e-tester', 'reports', 'qa-management']
    let allowed: boolean
    if (to.name === 'administration') {
      allowed = ADMIN_TAB_KEYS.some(k => canAccess(k))
    } else if (to.name === 'property-setup') {
      allowed = PROPERTY_SETUP_TAB_KEYS.some(k => canAccess(k))
    } else if (to.name === 'pms-settings') {
      allowed = canEnterPmsSettings(canAccess)
    } else if (to.name === 'raw-tables') {
      allowed = RAW_TABLE_KEYS.some(k => canAccess(k))
    } else if (to.name === 'dev-ops') {
      allowed = DEV_OPS_TAB_KEYS.some(k => canAccess(k))
    } else if (to.name === 'hotel-optimisation') {
      // Hosts the Block Manager tab, whose own `block-manager` key is a Core
      // permission while the page itself is Ops. Without this any-of rule,
      // moving that page in here would have silently taken Block Manager away
      // from everyone holding it but not Hotel Optimisation. The key list is
      // the page's own, so the door and the tabs cannot disagree.
      allowed = HOTEL_OPTIMISATION_TAB_KEYS.some(k => canAccess(k))
    } else {
      allowed = canAccess(to.name as string)
    }

    if (loaded.value && !allowed) {
      console.log('Access denied for page:', to.name)
      const firstPage = firstAccessiblePage()
      if (firstPage) {
        next({ name: firstPage })
      } else {
        next('/')
      }
      return
    }
  }

  next()
})

// Recover from stale chunk references after a deploy. Reload once per
// session window (the gate is short — 10s — so a *new* stale-bundle
// race after the reload still gets one more chance).
router.onError((err, to) => {
  if (!isStaleChunkError(err)) return
  try {
    const last = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) || 0)
    if (Date.now() - last < 10_000) return
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()))
  } catch { /* sessionStorage blocked — still try reload */ }
  // Navigate to the intended path on full reload so the user lands where
  // they were going, not back on the previous route.
  const target = (to && to.fullPath) || window.location.pathname + window.location.search
  window.location.replace(target)
})

// PostHog: initialize and capture page views
const { posthog } = usePostHog()

router.afterEach((to) => {
  // Tell AI which Vue route we're on so the next pageView (emitted by AI's
  // enableAutoRouteTracking) and every subsequent envelope (dependencies,
  // metrics, exceptions) gets `route` / `route_path` stamped on it. AI
  // otherwise reports every pageview as the document title ("ADG EventHub"),
  // making dashboards useless for per-route slicing.
  setCurrentRoute(to.name ? String(to.name) : null, to.path)
  if (posthog) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: to.name as string,
      page_path: to.path
    })
  }
})

export default router
